import { useLoaderData, useRevalidator, Link, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import OpenSeadragonAnnotate from '../../widgets/OpenSeadragonAnnotate';
import { useAuth } from '../../providers/AuthContext';
import styles from './item.module.css';

function ItemAnnotate() {
    const { itemId } = useParams();
    const { user, pending } = useAuth();
    const revalidator = useRevalidator();
    const hasRevalidated = useRef(false);
    const annotateRef = useRef(null);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const [saveError, setSaveError] = useState(null);
    const { ok, data, error } = useLoaderData();

    useEffect(() => {
        if (!pending && user && user._id && !ok && !hasRevalidated.current) {
            hasRevalidated.current = true;
            revalidator.revalidate();
        }
    }, [user, pending, ok, revalidator]);

    useEffect(() => {
        if (ok) hasRevalidated.current = false;
    }, [ok]);

    if (pending || revalidator.state === 'loading' || (!ok && user && user._id && !hasRevalidated.current)) {
        return <div>Loading...</div>;
    }

    if (!ok) {
        return (
            <>
                <h1>Oops, there was a problem</h1>
                <p>{error}</p>
            </>
        );
    }

    const payload = data;
    const hasViewer = payload.hasLargeImage && payload.tileMetadata && payload.girderItemId;
    const viewerProps = {
        girderItemId: payload.girderItemId,
        tileMetadata: payload.tileMetadata,
        style: { width: '100%', height: '100%' },
    };
    const title = payload.itemName || payload.networkFilename || 'Slide';

    async function handleSave() {
        if (!annotateRef.current?.save) return;
        setSaveStatus('saving');
        setSaveError(null);
        const result = await annotateRef.current.save();
        if (result.ok) {
            setSaveStatus('saved');
            setSaveError(null);
            setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
            setSaveStatus('error');
            setSaveError(result.error || 'Save failed');
        }
    }

    return (
        <div className={styles.viewerFullscreen}>
            <div className={styles.viewerTopBar}>
                <Link to={`/item/${itemId}`} className={styles.viewerTopBarBack}>
                    Back to item
                </Link>
                <span className={styles.viewerTopBarTitle}>{title}</span>
                {hasViewer && (
                    <button
                        type="button"
                        className={styles.viewerTopBarSave}
                        onClick={handleSave}
                        disabled={saveStatus === 'saving'}
                    >
                        {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                    </button>
                )}
                {saveStatus === 'saved' && <span className={styles.viewerTopBarSaved}>Saved</span>}
                {saveStatus === 'error' && saveError && (
                    <span className={styles.viewerTopBarError}>{saveError}</span>
                )}
            </div>
            <div className={styles.viewerFullscreenContent}>
                {hasViewer ? (
                    <OpenSeadragonAnnotate ref={annotateRef} {...viewerProps} />
                ) : (
                    <div className={styles.noViewer}>No whole-slide image available for this item.</div>
                )}
            </div>
        </div>
    );
}

export default ItemAnnotate;
